import styled from 'styled-components';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { useBreakpoints } from 'app/hooks';
import { useSearch } from 'features/search';
import { ButtonIcon, Flex, Input, Section, Typography } from 'components';
import { createUrl } from 'app/utils';
import { useEffect } from 'react';

const schema = yup
  .object()
  .shape({
    searchTerm: yup.string().required('Informe pelo menos 2 caracteres').min(2, 'Informe pelo menos 2 caracteres'),
  })
  .required();

const Opacity = styled(Flex)`
  position: fixed;
  width: 100%;
  z-index: 8;
  background: ${({ theme }) => theme.colors.black50};
  backdrop-filter: blur(3px);
  transition: opacity 0.5s ease 0s;
  pointer-events: none;

  ${({ theme: { mediaQueries } }) => {
    return `
      top: 60px;
      height: calc(100vh - 60px);

      ${mediaQueries.desktop} {
        top: 80px;
        height: calc(100vh - 80px);
      }
    `;
  }}
`;

const Wrapper = styled.div`
  background: ${({ theme }) => theme.colors.gray_100};
  width: 100%;
  z-index: 1009;
  transition: all 0.5s ease 0s;
`;

const Form = styled.form`
  flex-grow: 1;
  display: flex;
`;

const SearchInput = styled(Input)`
  ${({ theme: { mediaQueries, fontSizes, colors } }) => {
    return `
      font-size: ${fontSizes[1]};
      padding: 12px 12px 12px 35px;

      &::placeholder {
        font-size: ${fontSizes[1]};
        color: ${colors.black30};
      }

      ${mediaQueries.desktop} {
        font-size: ${fontSizes[2]};
        padding: 16px 16px 16px 60px;

        &::placeholder {
          font-size: ${fontSizes[2]};
          color: ${colors.black30};
        }
      }
    `;
  }}
`;

const SearchButtonIcon = styled(ButtonIcon)`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);

  ${({ theme: { mediaQueries } }) => {
    return `
      left: 10px;

      ${mediaQueries.desktop} {
        left: 20px;
      }
    `;
  }}
`;

interface SearchBarProps {
  open?: boolean;
}

const SearchBar = ({ open }: SearchBarProps) => {
  const { searchOpened, searchWrapperRef, searchInputRef, setSearchOpened, cleared, clearSearch, searched, fetchSearch } = useSearch();
  const breakpoint = useBreakpoints();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const searchTerm = searchParams.get('q') || '';
  const searchLimit = searchParams.get('limit');
  const productsTypes = searchParams.get('types');
  const enrolled = searchParams.get('enrolled');

  const {
    handleSubmit,
    setValue,
    formState: { errors },
    watch,
    reset,
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      searchTerm,
    },
  });

  const onSubmit = ({ searchTerm }: { searchTerm: string }) => {
    if (searched)
      fetchSearch({
        searchText: searchTerm,
        limit: searchLimit ? +searchLimit : 25,
        productTypes: productsTypes ? productsTypes.split(',').map((t) => +t) : [],
        enrolled: enrolled ? Boolean(enrolled) : null,
        withCache: false,
      });

    navigate(
      `${createUrl('search')}?q=${searchTerm}${searchLimit ? `&limit=${searchLimit}&page=1` : ''}${productsTypes ? `&types=${productsTypes}` : ''}${
        enrolled ? `&enrolled=${enrolled}` : ''
      }`
    );
    setSearchOpened(false);
  };

  useEffect(() => {
    if (cleared) {
      reset();
      clearSearch(true);
    }
  }, [cleared]);

  return (
    <>
      <Wrapper
        ref={searchWrapperRef}
        style={{
          position: open ? 'relative' : 'absolute',
          opacity: open || searchOpened ? 1 : 0,
          top: open ? 'inherit' : searchOpened ? (breakpoint === 'desktop' ? 80 : 60) : breakpoint === 'desktop' ? -80 : -60,
        }}
      >
        <Section flexDirection="column" py={{ m: 3, d: 4 }}>
          <Form onSubmit={handleSubmit(onSubmit)} style={{ position: 'relative' }}>
            <SearchInput
              ref={searchInputRef}
              name="searchTerm"
              onChange={(e) => {
                setValue('searchTerm', e.target.value);
              }}
              placeholder="Procurar por"
              autoFocus
              value={watch('searchTerm')}
            />

            <SearchButtonIcon
              type="submit"
              bg="transparent"
              color="primary_400"
              icon="search"
              iconWidth={breakpoint === 'desktop' ? 26 : 20}
              position="absolute"
            />
          </Form>

          {!!errors?.searchTerm && !!errors?.searchTerm?.message && (
            <Typography type="type1" color="alert_400" mt={2}>
              {errors?.searchTerm?.message}
            </Typography>
          )}
        </Section>
      </Wrapper>

      <Opacity opacity={searchOpened ? 1 : 0} />
    </>
  );
};

export default SearchBar;
